import { Table, type TableColumns, useDefaultSorting } from '@bp/ui-components';
import { BpLink } from 'components/BpLink/BpLink';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { niceDate } from '../../../utils/dateCalculations';
import { StatusChip } from '../../StatusChip/StatusChip';
import { Assignment, useAssignments } from '../hooks/useAssignments';

type AssignmentsStudentTableProps = {
  onNavigate: (uuid: string) => void;
};

export const AssignmentsStudentTable = ({ onNavigate }: AssignmentsStudentTableProps) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { sorting, onSortingChange } = useDefaultSorting();

  const { pastAssignments } = useAssignments({ courseUuid });

  const columns: TableColumns<Assignment>[] = [
    {
      id: 'title',
      accessorKey: 'title',
      header: t('common.name'),
      size: 250,
      canExpand: true,
      cell: ({ row }) => {
        return (
          <BpLink
            value={row.original.title}
            onNavigate={() => onNavigate(row.original.uuid)}
            allowAfterDue={row.original.allowSubmissionAfterDueDate ?? false}
          />
        );
      },
    },
    {
      id: 'visibleFrom',
      accessorKey: 'visibleFrom',
      header: t('common.activeFrom'),
      size: 150,
      cell: ({ row }) => niceDate(row.original.visibleFrom, 'medium'),
    },
    {
      id: 'dueDate',
      accessorKey: 'dueDate',
      header: t('assignments.dueAt'),
      size: 150,
      cell: ({ row }) => niceDate(row.original.dueDate, 'medium'),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('common.status'),
      size: 150,
      cell: ({ row }) => {
        if (row.original.submissions[0]) return <StatusChip status={row.original.submissions[0].status} />;
        return <></>;
      },
    },
  ];

  return (
    <Table<Assignment>
      showActionBar
      actionBarSettings={{
        marginBottom: 'var(--spacing-3)',
      }}
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      columns={columns}
      data={pastAssignments}
      hideHeader={pastAssignments.length === 0}
      customPadding='var(--spacing-7)'
      emptyStateSettings={{
        size: 'small',
        subtitle: t('assignments.noPast'),
        title: '',
        hideIcon: true,
        padding: 'l',
      }}
    />
  );
};
